import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

const GRILLES = ["DEFAUT", "BOLLORE", "TPNO", "YNERGIE"];
const BASE = [
  ["prixGlobal", "Prix global classique"],
  ["prixGlobalPrecarite", "Prix global précarité"],
  ["prixMax", "Prix max partenaire classique"],
  ["prixMaxPrecarite", "Prix max partenaire précarité"],
  ["prixMin", "Prix min classique"],
  ["prixMinPrecarite", "Prix min précarité"],
];
const n = (v) => parseFloat(v) || 0;
const sortP = (ps) => [...(ps || [])].sort((a, b) => n(b.seuilKwhCumac) - n(a.seuilKwhCumac));

const Field = ({ label, tid, value, onChange, step = "0.05" }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">{label}</span>
    <input data-testid={tid} type="number" min="0" step={step} className="field-input font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

const Paliers = ({ g, tab, update }) => {
  const setP = (i, patch) => update({ paliers: g.paliers.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-3">
      {g.paliers.map((p, i) => (
        <div key={i} className="grid grid-cols-1 gap-3 rounded-xl border border-[var(--line)] bg-[var(--inset)] p-4 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <Field label={`Seuil palier ${i + 1} (kWh cumac, >)`} step="1" tid={`admin-${tab}-seuil-${i}`} value={p.seuilKwhCumac} onChange={(v) => setP(i, { seuilKwhCumac: v })} />
          <Field label="Max classique (€/MWh)" tid={`admin-${tab}-palier-max-${i}`} value={p.prixMax} onChange={(v) => setP(i, { prixMax: v })} />
          <Field label="Max précarité (€/MWh)" tid={`admin-${tab}-palier-max-prec-${i}`} value={p.prixMaxPrecarite} onChange={(v) => setP(i, { prixMaxPrecarite: v })} />
          <div className="flex items-end">
            <button type="button" data-testid={`admin-${tab}-remove-palier-${i}`} title="Supprimer ce palier"
              onClick={() => update({ paliers: g.paliers.filter((_, j) => j !== i) })} className="btn-ghost h-[42px] px-3 text-[var(--rose-text)]">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      <button data-testid={`admin-${tab}-add-palier-btn`} className="btn-ghost w-full"
        onClick={() => update({ paliers: [...g.paliers, { seuilKwhCumac: 0, prixMax: g.prixMax, prixMaxPrecarite: g.prixMaxPrecarite }] })}>
        <Plus size={14} /> Ajouter un palier de volume
      </button>
    </div>
  );
};

const Recap = ({ grilles }) => {
  const [share, setShare] = useState(51);
  const s = Math.min(100, Math.max(0, n(share))) / 100;
  const pond = (c, p) => n(c) * (1 - s) + n(p) * s;
  return (
    <div data-testid="admin-grilles-recap" className="overflow-x-auto rounded-xl border border-[var(--line)] bg-[var(--inset)] p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-[var(--mute)]">
        Récapitulatif des prix pondérés (€/MWh, prix de base hors paliers) pour une part précarité de
        <input data-testid="admin-recap-share" type="number" min="0" max="100" className="field-input w-20 py-1 font-mono" value={share} onChange={(e) => setShare(e.target.value)} /> %
      </div>
      <table className="w-full min-w-[480px] text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--mute)]">
            {["Grille", "Global", "Max", "Min", "Part Genis"].map((h) => <th key={h} className="py-1.5">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {GRILLES.map((k) => {
            const g = grilles[k];
            const gl = pond(g.prixGlobal, g.prixGlobalPrecarite), mx = pond(g.prixMax, g.prixMaxPrecarite), mn = pond(g.prixMin, g.prixMinPrecarite);
            return (
              <tr key={k} data-testid={`admin-recap-${k}`} className="border-t border-[var(--line-soft)] font-mono text-[var(--ink)]">
                <td className="py-1.5 font-sans font-semibold">{k === "DEFAUT" ? "Par défaut" : k}</td>
                {[gl, mx, mn, gl - mx].map((v, i) => <td key={i} className="py-1.5">{v.toFixed(2).replace(".", ",")}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const PricingGrilles = () => {
  const [pricing, setPricing] = useState(null);
  const [tab, setTab] = useState("DEFAUT");
  const [saving, setSaving] = useState(false);
  const norm = (d) => ({ ...d, grilles: Object.fromEntries(GRILLES.map((k) => [k, { ...d.grilles[k], paliers: sortP(d.grilles[k].paliers) }])) });

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(norm(data))).catch((e) => toast.error(apiErrorMessage(e)));
  }, []);

  if (!pricing) return null;
  const g = pricing.grilles[tab];
  const update = (patch) => setPricing((p) => ({ ...p, grilles: { ...p.grilles, [tab]: { ...p.grilles[tab], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const grilles = Object.fromEntries(GRILLES.map((k) => {
        const x = pricing.grilles[k];
        return [k, { ...Object.fromEntries(BASE.map(([f]) => [f, n(x[f])])),
          paliers: x.paliers.map((p) => ({ seuilKwhCumac: n(p.seuilKwhCumac), prixMax: n(p.prixMax), prixMaxPrecarite: n(p.prixMaxPrecarite) })) }];
      }));
      const { data } = await api.put("/admin/pricing", { colonnePrecarite: pricing.colonnePrecarite || "A", grilles });
      setPricing(norm(data));
      toast.success("Grilles de prix enregistrées — appliquées aux prochains calculs.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-pricing-grilles">
      <div className="flex flex-wrap gap-2">
        {GRILLES.map((k) => (
          <button key={k} type="button" data-testid={`admin-grille-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${tab === k ? "border-sky-500/60 bg-sky-500/10 text-[var(--accent-text)]" : "border-[var(--line)] text-[var(--dim)]"}`}>
            {k === "DEFAUT" ? "Par défaut" : k}
          </button>
        ))}
      </div>
      <p className="text-[11px] leading-relaxed text-[var(--mute)]">
        {tab === "DEFAUT" ? "Grille appliquée tant qu'aucun Obligé n'est attribué au dossier." : `Grille appliquée dès que le dossier est attribué à ${tab}.`}{" "}
        Le curseur partenaire varie entre le prix min et le prix max pondérés (selon la part précarité du département) ; la part GENIS est l'écart entre le prix global et le prix max pondérés.
      </p>
      <div className="grid grid-cols-2 gap-3 rounded-xl border border-sky-500/25 bg-sky-500/5 p-4">
        {BASE.map(([k, label]) => (
          <Field key={k} label={`${label} (€/MWh)`} tid={`admin-${tab}-${k}`} value={g[k]} onChange={(v) => update({ [k]: v })} />
        ))}
        <p className="col-span-2 text-[11px] text-[var(--mute)]">Les prix max ci-dessus s'appliquent sous le premier seuil de volume.</p>
      </div>
      <Paliers g={g} tab={tab} update={update} />
      <Recap grilles={pricing.grilles} />
      <div data-testid="admin-precarite-colonne" className="rounded-xl border border-[var(--line)] bg-[var(--inset)] p-4">
        <p className="mb-2 text-[11px] leading-relaxed text-[var(--mute)]">
          Part précarité du volume selon le département (annexe I de l'arrêté du 29/12/2014 modifié) — colonne utilisée :
        </p>
        <div className="flex gap-2">
          {[["A", "Colonne A (grande précarité)"], ["B", "Colonne B (précarité)"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`admin-colonne-${k}`} onClick={() => setPricing((p) => ({ ...p, colonnePrecarite: k }))}
              className={`rounded-lg border px-3 py-2 text-xs font-medium ${(pricing.colonnePrecarite || "A") === k ? "border-sky-500/60 bg-sky-500/10 text-[var(--accent-text)]" : "border-[var(--line)] text-[var(--dim)]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end">
        <button data-testid="admin-save-pricing-btn" onClick={save} disabled={saving} className="btn-primary">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer les grilles
        </button>
      </div>
    </div>
  );
};
